"use client";
import React, { useEffect, useState, useMemo } from "react";
import Link from "next/link";
import { Users, Maximize, Eye, Bed } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import {
  getAllActiveAmenities,
  getAllActiveRooms,
  getAllActiveTestimonials,
  getAllCMSByURL,
  getAllMetaData,
} from "@/store/home/home.services";
import Banner from "@/components/Banner";
import { OptimizedImage } from "@/components/common/OptimizedImage";
import { LoadingPanel } from "@/components/common/LoadingPanel";
import { ICMS, IRoom } from "@/store/home/homeModel";
import { cleanHeaderTags, createLookupObject } from "@/lib/helper";

function RoomCard({ room }: { room: IRoom }) {
  return (
    <div className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#E8DCC8]">
      <div className="relative h-80 overflow-hidden">
        <OptimizedImage
          src={room?.Image}
          alt={room?.RoomTitle}
          width={800}
          height={600}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute top-4 right-4 bg-[#E07A2F] text-white px-4 py-2 rounded-full text-sm font-medium">
          {room?.RoomCategory}
        </div>
      </div>
      <div className="p-8">
        <h3
          className="text-2xl mb-2"
          style={{ fontFamily: "Playfair Display, serif" }}
        >
          {room?.RoomTitle}
        </h3>
        <p className="text-[#7A4A1D] italic mb-6 text-lg">
          {room?.Description}
        </p>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-3 text-[#5A3A1E]">
            <Maximize className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
            <span className="text-sm">{room?.SqFoot}</span>
          </div>
          <div className="flex items-center gap-3 text-[#5A3A1E]">
            <Users className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
            <span className="text-sm">{room?.Adults}</span>
          </div>
          <div className="flex items-center gap-3 text-[#5A3A1E]">
            <Bed className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
            <span className="text-sm">{room?.BedSize}</span>
          </div>
          <div className="flex items-center gap-3 text-[#5A3A1E]">
            <Eye className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
            <span className="text-sm">{room?.RoomViewType}</span>
          </div>
        </div>
        <div className="mb-6 pb-6 border-b border-[#E8DCC8]">
          <span
            className="text-3xl text-[#1F3D2B]"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            ₹{room?.Rate}
          </span>
          <span className="text-[#7A4A1D]/85"> / night</span>
        </div>
        <div className="flex gap-4">
          <Link
            href="/contact-us/"
            className="flex-1 text-center bg-gradient-to-r from-[#E07A2F] to-[#c96a28] text-white px-6 py-3 rounded-full hover:shadow-xl transition-all font-medium hover:scale-105"
          >
            Inquire now
          </Link>
        </div>
      </div>
    </div>
  );
}

export interface RoomsProps {
  cmsData: ICMS[];
  bannerData: any[];
  roomData: IRoom[];
  testimonialData: any[];
  amenitiesData: any[];
}

export default function Rooms({
  cmsData,
  bannerData,
  roomData,
  testimonialData,
  amenitiesData,
}: RoomsProps) {
  const RoomList = roomData;
  const TestimonialList = testimonialData;
  const AmenitiesList = amenitiesData;

  const [selectedCategory, setSelectedCategory] = useState("All Rooms");
  const [isSticky, setIsSticky] = useState(false);

  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const filteredRooms =
    selectedCategory === "All Rooms"
      ? RoomList
      : RoomList.filter((room) => room.RoomCategory === selectedCategory);
  const featuredRoom = RoomList?.filter((room) => room.IsFeatured)[0];

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "rooms") || [];

  const categories = [
    "All Rooms",
    "Deluxe Cottages",
    "Family Cottages",
    "Tent Cottages",
  ];

  return (
    <>
      <Banner BannerList={banner} />

      {/* Category Switcher */}
      <div
        className={`sticky top-24 z-40 transition-all duration-300 ${
          isSticky ? "bg-white shadow-lg" : "bg-[#F3EBDD]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex gap-3 overflow-x-auto hide-scrollbar">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full whitespace-nowrap font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-[#7A4A1D] text-white shadow-lg"
                    : "bg-white text-[#7A4A1D] hover:bg-[#7A4A1D]/10 border border-[#E8DCC8]"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Room */}
      {featuredRoom && (
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-br from-white to-[#F3EBDD] rounded-3xl overflow-hidden shadow-2xl border border-[#E8DCC8]">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="h-[500px] md:h-auto relative overflow-hidden">
                  <OptimizedImage
                    src={featuredRoom?.Image}
                    alt={featuredRoom?.RoomTitle}
                    width={800}
                    height={600}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-6 left-6 bg-[#E07A2F] text-white px-5 py-2 rounded-full font-medium">
                    Featured Room
                  </div>
                </div>
                <div className="p-12 flex flex-col justify-center">
                  <h2
                    className="text-4xl mb-4"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    {featuredRoom?.RoomTitle}
                  </h2>
                  <p className="text-xl text-[#BF5500] italic mb-8">
                    "{featuredRoom?.RoomCategory}"
                  </p>
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E07A2F]/10 flex items-center justify-center">
                        <Maximize className="w-6 h-6 text-[#E07A2F]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#7A4A1D]/85">Size</p>
                        <p className="font-medium text-[#1F3D2B]">
                          {featuredRoom?.SqFoot}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E07A2F]/10 flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#E07A2F]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#7A4A1D]/85">Occupancy</p>
                        <p className="font-medium text-[#1F3D2B]">
                          {featuredRoom?.Adults}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E07A2F]/10 flex items-center justify-center">
                        <Eye className="w-6 h-6 text-[#E07A2F]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#7A4A1D]/85">View</p>
                        <p className="font-medium text-[#1F3D2B]">
                          {featuredRoom?.RoomViewType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E07A2F]/10 flex items-center justify-center">
                        <Bed className="w-6 h-6 text-[#E07A2F]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#7A4A1D]/85">Bed type</p>
                        <p className="font-medium text-[#1F3D2B]">
                          {featuredRoom?.BedSize}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-8">
                    <span
                      className="text-4xl text-[#1F3D2B]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      ₹{featuredRoom?.Rate}
                    </span>
                    <span className="text-[#7A4A1D]/85 text-lg"> / night</span>
                  </div>
                  <Link
                    href="/contact-us/"
                    className="text-center bg-gradient-to-r from-[#E07A2F] to-[#c96a28] text-white px-8 py-4 rounded-full hover:shadow-2xl transition-all font-medium text-lg hover:scale-105"
                  >
                    Inquire now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Rooms Listing */}
      <section id="rooms" className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-16"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData["Rooms - Section 1 : Our Story"]?.Template,
              ),
            }}
          />
          <div className="grid md:grid-cols-2 gap-8">
            {filteredRooms.map((room) => (
              <RoomCard key={room.ID} room={room} />
            ))}
          </div>
          {filteredRooms.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-[#7A4A1D]">
                No rooms found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Why Our Rooms Are Special */}
      {lookUpCMSData["Rooms - Section 2 : Why Our Rooms Are Special"]
        ?.Template && (
        <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-[#1F3D2B] to-[#2A4F38] text-white">
          <div
            className="max-w-7xl mx-auto"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData["Rooms - Section 2 : Why Our Rooms Are Special"]
                  ?.Template,
              ),
            }}
          />
        </section>
      )}

      {/* Plan My Stay */}
      {lookUpCMSData["Rooms - Section 3 : Plan My Stay"]?.Template && (
        <section className="relative py-32 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <OptimizedImage
              src={lookUpCMSData["Rooms - Section 3 : Plan My Stay"]?.CMSImage}
              alt={
                lookUpCMSData["Rooms - Section 3 : Plan My Stay"]
                  ?.CMSImageAlterTag || "Plan My Stay"
              }
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
            <div
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(
                  lookUpCMSData["Rooms - Section 3 : Plan My Stay"]?.Template,
                ),
              }}
            />
            <Link
              href="/contact-us/"
              className="inline-block bg-gradient-to-r from-[#E07A2F] to-[#c96a28] text-white px-12 py-5 rounded-full hover:shadow-2xl transition-all font-medium text-lg hover:scale-105"
            >
              Plan My Stay
            </Link>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-20 px-6 lg:px-8 bg-[#F3EBDD]">
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-16"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData["Rooms - Section 4 : Testimonials"]?.Template,
              ),
            }}
          />
          <div className="grid md:grid-cols-3 gap-8">
            {TestimonialList.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-3xl shadow-lg border border-[#E8DCC8]"
              >
                <p
                  className="text-[#5A3A1E] mb-4 italic"
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(testimonial.Description),
                  }}
                />
                <p className="text-[#7A4A1D] font-medium">
                  — {testimonial.CustomerName}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div
            className="text-center mb-16"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData["Rooms - Section 5 : Premium Amenities"]
                  ?.Template,
              ),
            }}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AmenitiesList.map((amenity, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-6 bg-[#F3EBDD] rounded-2xl hover:shadow-lg transition-all"
              >
                <OptimizedImage
                  src={amenity.Image}
                  alt={amenity.Title}
                  width={32}
                  height={32}
                  className="w-8 h-8"
                />
                <span className="font-medium text-[#1F3D2B]">
                  {amenity.Title}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
